"use client"

import { useEffect, useState, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useMediaQuery } from 'react-responsive';
import { Minus, Plus, Search, Loader2, ShoppingCart, X } from "lucide-react"
import "./index.css"

import { useSelector } from "react-redux"
import { useCart } from "../../context/CartContext"

const ProductGrid = ({ products, loading, error }) => {
  const navigate = useNavigate()
  const [animatingCounters, setAnimatingCounters] = useState({})
  const [animationDirection, setAnimationDirection] = useState({})
  const [searchTerm, setSearchTerm] = useState("")
  const [sortOption, setSortOption] = useState("default")
  const [visibleProducts, setVisibleProducts] = useState(8)
  const [isLoading, setIsLoading] = useState(false)
  const loaderRef = useRef(null)
  const [currentSlides, setCurrentSlides] = useState({})
  const slidersRef = useRef({})
  const slideTimersRef = useRef({})
  const touchStartXRef = useRef({})
  const [isMobile, setIsMobile] = useState(false)
  const slideIntervalRef = useRef(null)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const { cart, addToCart, updateQuantity } = useCart()
  const user = useSelector((state) => state.auth.user)
  const isMobiles = useMediaQuery({ query: '(max-width: 768px)' });

  const formatPrice = (price) => {
    const parts = price.toFixed(2).split(".")
    return {
      dollars: parts[0],
      cents: parts[1],
    }
  }

  const handleAddToCart = (productId) => {
    if (!user) {
      setShowLoginModal(true)
      return
    }

    setAnimationDirection((prev) => ({
      ...prev,
      [productId]: "up",
    }))

    setAnimatingCounters((prev) => ({
      ...prev,
      [productId]: true,
    }))

    setTimeout(() => {
      setAnimatingCounters((prev) => ({
        ...prev,
        [productId]: false,
      }))
    }, 500)
    addToCart(productId, 1)
  }

  const increaseQuantity = (productId) => {
    setAnimationDirection((prev) => ({
      ...prev,
      [productId]: "up",
    }))

    setAnimatingCounters((prev) => ({
      ...prev,
      [productId]: true,
    }))

    setTimeout(() => {
      setAnimatingCounters((prev) => ({
        ...prev,
        [productId]: false,
      }))
    }, 500)
    updateQuantity(productId, (cart[productId] || 0) + 1)
  }

  const decreaseQuantity = (productId) => {
    setAnimationDirection((prev) => ({
      ...prev,
      [productId]: "down",
    }))

    setAnimatingCounters((prev) => ({
      ...prev,
      [productId]: true,
    }))

    setTimeout(() => {
      setAnimatingCounters((prev) => ({
        ...prev,
        [productId]: false,
      }))
    }, 500)
    updateQuantity(productId, (cart[productId] || 0) - 1)
  }

  const getAnimationClass = (itemKey) => {
    if (!animatingCounters[itemKey]) return ""

    return animationDirection[itemKey] === "up" ? "counter-animation-up" : "counter-animation-down"
  }

  const getFilteredAndSortedProducts = useCallback(() => {
    if (!Array.isArray(products)) return []

    const filtered = products.filter(
      (product) =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    switch (sortOption) {
      case "price-low-high":
        return filtered.sort((a, b) => a.mrp - b.mrp)
      case "price-high-low":
        return filtered.sort((a, b) => b.mrp - a.mrp)
      case "name-a-z":
        return filtered.sort((a, b) => a.name.localeCompare(b.name))
      case "name-z-a":
        return filtered.sort((a, b) => b.name.localeCompare(a.name))
      default:
        return filtered
    }
  }, [products, searchTerm, sortOption])

  const displayProducts = getFilteredAndSortedProducts().slice(0, visibleProducts)

  const loadMoreProducts = useCallback(() => {
    setIsLoading(true)
    setTimeout(() => {
      setVisibleProducts((prev) => Math.min(prev + 4, getFilteredAndSortedProducts().length))
      setIsLoading(false)
    }, 800)
  }, [getFilteredAndSortedProducts])

  const nextSlide = (productId) => {
    const product = products.find((p) => p._id === productId)
    if (!product) return

    const combinedImages = [product.mainImage, ...(product.otherImages || [])]
    const imageCount = combinedImages.length
    if (imageCount <= 1) return

    setCurrentSlides((prev) => {
      const currentIndex = prev[productId] || 0
      const nextIndex = (currentIndex + 1) % imageCount
      return { ...prev, [productId]: nextIndex }
    })
  }

  const prevSlide = (productId) => {
    const product = products.find((p) => p._id === productId)
    if (!product) return

    const combinedImages = [product.mainImage, ...(product.otherImages || [])]
    const imageCount = combinedImages.length
    if (imageCount <= 1) return

    setCurrentSlides((prev) => {
      const currentIndex = prev[productId] || 0
      const prevIndex = (currentIndex - 1 + imageCount) % imageCount
      return { ...prev, [productId]: prevIndex }
    })
  }

  const navigateToSingleProduct = (productId) => {
    navigate(`/single-product?id=${productId}`)
  }

  const handleMouseEnter = (productId) => {

    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current)
    }

    const product = products.find((p) => p._id === productId)
    if (product && product.otherImages && product.otherImages.length > 0) {
      slideIntervalRef.current = setInterval(() => {
        nextSlide(productId)
      }, 1500)
    }
  }

  const handleMouseLeave = () => {

    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current)
      slideIntervalRef.current = null
    }
  }

  const handleTouchStart = (productId, e) => {
    touchStartXRef.current[productId] = e.touches[0].clientX
  }

  const handleTouchEnd = (productId, e) => {
    if (!touchStartXRef.current[productId]) return

    const touchEndX = e.changedTouches[0].clientX
    const diff = touchStartXRef.current[productId] - touchEndX

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextSlide(productId)
      } else {
        prevSlide(productId)
      }
    }

    touchStartXRef.current[productId] = null
  }

  const getBoxClass = (index) => {
    const boxClasses = ["box1", "box2", "box3", "box4"]
    return boxClasses[index % boxClasses.length]
  }

  useEffect(() => {
    const currentSlideInterval = slideIntervalRef.current
    const currentSlideTimers = { ...slideTimersRef.current }

    return () => {
      if (currentSlideInterval) {
        clearInterval(currentSlideInterval)
      }

      Object.keys(currentSlideTimers).forEach((productId) => {
        if (currentSlideTimers[productId]) {
          clearInterval(currentSlideTimers[productId])
        }
      })
    }
  }, [])

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 640)
    }

    checkIfMobile()
    window.addEventListener("resize", checkIfMobile)

    return () => {
      window.removeEventListener("resize", checkIfMobile)
    }
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && !isLoading && visibleProducts < getFilteredAndSortedProducts().length) {
          loadMoreProducts()
        }
      },
      { threshold: 0.1 },
    )

    const currentLoaderRef = loaderRef.current
    if (currentLoaderRef) {
      observer.observe(currentLoaderRef)
    }

    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef)
      }
    }
  }, [visibleProducts, isLoading, searchTerm, sortOption, getFilteredAndSortedProducts, loadMoreProducts])

  return (
    <>
      <section className="classic-con-product-grid main-shop-page">
        <figure className="classic-leftimage-product-grid">
          <img
            src="https://res.cloudinary.com/dzk6ng1ba/image/upload/v1743069237/T01_hwn5yp.png"
            alt="image-0"
            className="img-fluid-product-grid"
            style={{ display: "none" }}
          />
        </figure>
        <figure className="classic-rightimage-product-grid" style={{ transform: "rotate(60deg)" }}>
          <img
            src="https://res.cloudinary.com/dzk6ng1ba/image/upload/v1743069238/T03_myejap.png"
            alt="image-1"
            className="img-fluid-product-grid"
            style={{ display: "none" }}
          />
        </figure>
        <div className="creation-items-product-grid">
          <div className="controls-product-grid">
            <div className="search-container-product-grid">
              <Search className="search-icon-product-grid" size={18} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-product-grid"
              />
            </div>
            <div className="sort-container-product-grid">
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="sort-select-product-grid"
              >
                <option value="default" className="sort-option-product-grid">
                  Default
                </option>
                <option value="price-low-high" className="sort-option-product-grid">
                  Price: Low to High
                </option>
                <option value="price-high-low" className="sort-option-product-grid">
                  Price: High to Low
                </option>
                <option value="name-a-z" className="sort-option-product-grid">
                  Name: A to Z
                </option>
                <option value="name-z-a" className="sort-option-product-grid">
                  Name: Z to A
                </option>
              </select>
            </div>
          </div>

          <div className="classic-wrapper-product-grid" data-aos="fade-up">
            <div className="grid-product-grid">
              {loading ? (
                <div className="loading-product-grid">
                  <Loader2 className="loading-icon-product-grid" />
                  <span className="loading-text-product-grid">Loading products...</span>
                </div>
              ) : error ? (
                <div className="error-product-grid">
                  <p className="error-text-product-grid">Error loading products. Please try again.</p>
                </div>
              ) : (
                Array.isArray(products) &&
                displayProducts.map((product, index) => {
                  const priceParts = formatPrice(product.mrp)
                  const sellerParts = formatPrice(product.sellingPrice)
                  const animationClass = getAnimationClass(product._id)
                  const currentSlideIndex = currentSlides[product._id] || 0
                  const boxClass = getBoxClass(index)
                  const combinedBothImages = [product.mainImage, ...(product.otherImages || [])]
                  // const isHovered = hoveredProduct === product._id

                  return (
                    <div
                      className="classic-box-product-grid"
                      key={product._id}
                      onMouseEnter={() => handleMouseEnter(product._id)}
                      onMouseLeave={handleMouseLeave}
                    >

                      <div
                        className={`classic-image-box-product-grid ${boxClass}`}
                        style={{
                          height: isMobiles ? '170px' : '300px', // Adjust height based on screen size
                          overflow: 'hidden',
                          position: 'relative'
                        }}
                      >
                        <div
                          className="image-slider-product-grid"
                          ref={(el) => (slidersRef.current[product._id] = el)}
                          onTouchStart={(e) => handleTouchStart(product._id, e)}
                          onTouchEnd={(e) => handleTouchEnd(product._id, e)}
                          onClick={() => navigateToSingleProduct(product._id)}
                          style={{
                            cursor: "pointer",
                            position: "relative",
                            height: "100%",
                            width: "100%"
                          }}
                        >
                          <div
                            className="slider-container-product-grid"
                            style={{
                              transform: `translateX(-${currentSlideIndex * 100}%)`,
                              height: "100%",
                              width: "100%",
                              display: "flex"
                            }}
                          >
                            {combinedBothImages.map((image, idx) => (
                              <div
                                className="slide-product-grid"
                                key={idx}
                                style={{
                                  minWidth: "100%",
                                  height: "100%",
                                  flexShrink: 0
                                }}
                              >
                                <img
                                  src={image || "/placeholder.svg"}
                                  alt={`${product.name} - ${idx + 1}`}
                                  className="slide-img-product-grid"
                                  style={{
                                    width: "100%",
                                    height: "75%",
                                    // Ensure images maintain aspect ratio and cover container
                                    objectPosition: "center"
                                  }}
                                />
                              </div>
                            ))}
                          </div>

                          {/* {isHovered && combinedBothImages.length > 1 && (
                            <>
                              <button
                                className="slider-nav-btn prev-btn"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  prevSlide(product._id)
                                }}
                                style={{
                                  position: "absolute",
                                  left: "5px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  background: "rgba(255, 255, 255, 0.7)",
                                  borderRadius: "50%",
                                  width: "30px",
                                  height: "30px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "none",
                                  cursor: "pointer",
                                  zIndex: 2,
                                }}
                              >
                                <ChevronLeft size={18} />
                              </button>
                              <button
                                className="slider-nav-btn next-btn"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  nextSlide(product._id)
                                }}
                                style={{
                                  position: "absolute",
                                  right: "5px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  background: "rgba(255, 255, 255, 0.7)",
                                  borderRadius: "50%",
                                  width: "30px",
                                  height: "30px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "none",
                                  cursor: "pointer",
                                  zIndex: 2,
                                }}
                              >
                                <ChevronRight size={18} />
                              </button>
                              <div
                                className="slider-indicators"
                                style={{
                                  position: "absolute",
                                  bottom: "10px",
                                  left: "0",
                                  right: "0",
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: "5px",
                                  zIndex: 2,
                                }}
                              >
                                {combinedBothImages.map((_, idx) => (
                                  <span
                                    key={idx}
                                    className={`indicator ${currentSlideIndex === idx ? "active" : ""}`}
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      borderRadius: "50%",
                                      background: currentSlideIndex === idx ? "#feba2a" : "rgba(255, 255, 255, 0.7)",
                                      cursor: "pointer",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setCurrentSlides((prev) => ({ ...prev, [product._id]: idx }))
                                    }}
                                  />
                                ))}
                              </div>
                            </>
                          )} */}
                        </div>
                      </div>
                      <div className="classic-box-content-product-grid">
                        <div className="text-wrapper-product-grid">
                          <h6 className="product-title-product-grid">{product.name.split(' ').slice(0, 2).join(' ')}</h6>
                          <div className="rating-product-grid">
                            <i className="fa-solid fa-star star-product-grid"></i>
                            <span className="rating-text-product-grid">4.9/5</span>
                          </div>
                        </div>
                        <p className="product-description-product-grid">{product.description.slice(0, 30)}</p>
                        <div className="price-wrapper-product-grid">
                          <span className="price-product-grid">
                            <span className="price-dollars-product-grid">₹{sellerParts.dollars} </span>
                            <span className="line-through-price">₹{priceParts.dollars}</span>
                          </span>

                          <div className="quantity-selector-product-grid">
                            {cart && cart[product._id] ? (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    decreaseQuantity(product._id)
                                  }}
                                  className="quantity-btn-minus-product-grid"
                                >
                                  <Minus size={isMobile ? 14 : 16} className="quantity-icon-product-grid" />
                                </button>
                                <span className={`quantity-value-product-grid ${animationClass}`}>
                                  {cart[product._id]}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    increaseQuantity(product._id)
                                  }}
                                  className="quantity-btn-plus-product-grid"
                                >
                                  <Plus size={isMobile ? 14 : 16} className="quantity-icon-product-grid" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleAddToCart(product._id)
                                  if (typeof window.fbq === "function") {
                                    window.fbq("track", "AddToCart", {
                                      content_ids: [product._id],
                                      content_type: "product",
                                      value: product.price,
                                      currency: "INR",
                                    });
                                  }
                                }}

                                className="add-to-cart-btn-product-grid"
                                style={{ width: "100%" }}
                              >
                                {isMobile ? (
                                  <ShoppingCart
                                    size={16}
                                    className="cart-icon-product-grid"
                                    style={{ width: "100%" }}
                                  />
                                ) : (
                                  <span className="cart-text-product-grid">Add to Cart</span>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            {!loading && !error && displayProducts.length < getFilteredAndSortedProducts().length && (
              <div ref={loaderRef} className="loader-container-product-grid">
                {isLoading ? (
                  <div className="loading-product-grid">
                    <Loader2 className="loading-icon-product-grid" />
                    <span className="loading-text-product-grid">Loading more products...</span>
                  </div>
                ) : (
                  <div className="loader-placeholder-product-grid"></div>
                )}
              </div>
            )}

            {!loading && !error && displayProducts.length === 0 && (
              <div className="empty-results-product-grid">
                <p className="empty-text-product-grid">No products found matching your search.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {showLoginModal && (
        <div
          className="login-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="login-modal"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "20px",
              width: "90%",
              maxWidth: "400px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              className="login-modal-close"
              onClick={() => setShowLoginModal(false)}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                cursor: "pointer",
              }}
            >
              <X size={24} />
            </div>
            <h3
              className="login-modal-title"
              style={{
                fontSize: "1.5rem",
                marginBottom: "15px",
                textAlign: "center",
              }}
            >
              Login Required
            </h3>
            <div
              className="login-modal-content"
              style={{
                marginBottom: "20px",
                textAlign: "center",
              }}
            >
              <p>Please login to add items to your cart.</p>
            </div>
            <div
              className="login-modal-buttons"
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <button
                className="login-button"
                onClick={() => (window.location.href = "/login")}
                style={{
                  flex: 1,
                  padding: "10px",
                  backgroundImage: "linear-gradient(90deg, #e0a323 0%, #feba2a 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Login
              </button>

              <button
                className="cancel-button"
                onClick={() => setShowLoginModal(false)}
                style={{
                  flex: 1,
                  padding: "10px",
                  backgroundColor: "#e0e0e0",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  color: "black",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ProductGrid